import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { environment } from '../../../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { HousingInfoService } from '../../../../../services/housing-info/housing-info.service';
import { LoadingIndicatorService } from '../../../../../services/loading-indicator/loading-indicator.service';
import { EventsService } from '../../../../../services/events/events.service';

@Component({
  selector: 'app-housing-info-details-sources',
  templateUrl: './sources.component.html',
  styleUrls: ['./sources.component.css'],
})
export class HousingInfoDetailsSourcesComponent implements OnInit, OnChanges {
  @Input('housing') housing;
  form: FormGroup;
  isLoading: boolean = false;

  constructor(
    private http: HttpClient,
    private housingInfoService: HousingInfoService,
    private loadingIndicatorService: LoadingIndicatorService,
    private eventsService: EventsService
  ) {
    this.form = new FormGroup({
      bookingId: new FormControl({ value: '', disabled: true }),
      bookingWebsite: new FormControl(
        { value: '', disabled: true },
        Validators.compose([
          Validators.pattern('https:\\/\\/www\\.booking\\.com\\/hotel\\/.*'),
        ])
      ),
      expediaId: new FormControl({ value: '', disabled: true }),
      expediaWebsite: new FormControl({ value: '', disabled: true }),
      hrsId: new FormControl({ value: '', disabled: true }),
      hrsWebsite: new FormControl({ value: '', disabled: true }),
      tripadvisorId: new FormControl({ value: '', disabled: true }),
      tripadvisorWebsite: new FormControl({ value: '', disabled: true }),
      trivagoId: new FormControl(
        { value: '', disabled: true },
        Validators.compose([Validators.pattern('^\\d+$')])
      ),
      trivagoWebsite: new FormControl(
        { value: '', disabled: true },
        Validators.compose([
          Validators.pattern(
            'https:\\/\\/www\\.trivago\\.de\\/api\\/v1\\/_cache\\/accommodation\\/(\\d+)\\/complete-info.json.*'
          ),
        ])
      ),
      hostelworldId: new FormControl({ value: '', disabled: true }),
      hostelworldWebsite: new FormControl({ value: '', disabled: true }),
      travelclickId: new FormControl({ value: '', disabled: true }),
      travelclickWebsite: new FormControl({ value: '', disabled: true }),
      giataId: new FormControl({ value: '', disabled: true }),
      giataWebsite: new FormControl({ value: '', disabled: true }),
    });
  }

  onSubmitForm() {
    if (this.form.status !== 'VALID') {
      console.log('INVALID');
      this.validateAllFormFields(this.form);
      return;
    }
    this.showLoading();

    this.postData().subscribe(
      (response: any) => {
        this.hideLoading();
        this.housingInfoService.setSelectedHousing(response);
      },
      (err) => {
        this.hideLoading();
        if (err.error.message === 'E_EXTERNAL_SOURCE_OFFER_ENABLED') {
          this.eventsService.broadcast(
            'requestShowErrorMessage',
            'ID cannot be empty while external offers are enabled for this housing. Please either provide a valid ID or disable external offers for this housing first.'
          );
          return;
        } else if (err.error.message === 'E_VALIDATION_FAILED') {
          try {
            for (const error of err.error.errors) {
              this.form.get(error.property).setErrors(error.constraints);
              this.form.get(error.property).markAsDirty();
            }
          } catch (error) {
            console.log('Catched E', error);
          }
          return;
        }

        this.eventsService.broadcast(
          'requestShowErrorMessage',
          JSON.stringify(err)
        );
      }
    );
  }

  private postData() {
    return this.http.patch(
      environment.urls.service +
        '/api/v1/authenticated/admin/housing/' +
        this.housing._id +
        '/sources',
      {
        bookingId: this.createNullIfEmptyString(
          this.form.get('bookingId').value
        ),
        bookingWebsite: this.createNullIfEmptyString(
          this.form.get('bookingWebsite').value
        ),
        expediaId: this.createNullIfEmptyString(
          this.form.get('expediaId').value
        ),
        expediaWebsite: this.createNullIfEmptyString(
          this.form.get('expediaWebsite').value
        ),
        hrsId: this.createNullIfEmptyString(this.form.get('hrsId').value),
        hrsWebsite: this.createNullIfEmptyString(
          this.form.get('hrsWebsite').value
        ),
        tripadvisorId: this.createNullIfEmptyString(
          this.form.get('tripadvisorId').value
        ),
        tripadvisorWebsite: this.createNullIfEmptyString(
          this.form.get('tripadvisorWebsite').value
        ),
        trivagoId: this.createNullIfEmptyString(
          this.form.get('trivagoId').value
        ),
        trivagoWebsite: this.createNullIfEmptyString(
          this.form.get('trivagoWebsite').value
        ),
        hostelworldId: this.createNullIfEmptyString(
          this.form.get('hostelworldId').value
        ),
        hostelworldWebsite: this.createNullIfEmptyString(
          this.form.get('hostelworldWebsite').value
        ),
        travelclickId: this.createNullIfEmptyString(
          this.form.get('travelclickId').value
        ),
        travelclickWebsite: this.createNullIfEmptyString(
          this.form.get('travelclickWebsite').value
        ),
        giataId: this.createNullIfEmptyString(this.form.get('giataId').value),
        giataWebsite: this.createNullIfEmptyString(
          this.form.get('giataWebsite').value
        ),
      }
    );
  }

  private createNullIfEmptyString(s: string) {
    if (s != null && typeof s === 'string' && s.trim() === '') {
      return null;
    }
    return s;
  }

  ngOnInit() {
    if (this.housing != null) {
      this.mapHousingToForm();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.housing.currentValue != null) {
      this.housing = changes.housing.currentValue;
      this.mapHousingToForm();
    }
  }

  private validateAllFormFields(formGroup: FormGroup) {
    //{1}
    Object.keys(formGroup.controls).forEach((field) => {
      //{2}
      const control = formGroup.get(field); //{3}
      if (control instanceof FormControl) {
        //{4}
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        //{5}
        this.validateAllFormFields(control); //{6}
      }
    });
  }

  private mapHousingToForm() {
    const vals = {
      bookingId: this.housing.sourceIds.bk,
      bookingWebsite: this.housing.sourcePages.bk,
      hrsId: this.housing.sourceIds.hrs,
      hrsWebsite: this.housing.sourcePages.hrs,
      tripadvisorId: this.housing.sourceIds.ta,
      tripadvisorWebsite: this.housing.sourcePages.ta,
      trivagoId: this.housing.sourceIds.tg,
      trivagoWebsite: this.housing.sourcePages.tg,
      expediaId:
        this.housing.sourceIds.ex != null ? this.housing.sourceIds.ex : '',
      expediaWebsite:
        this.housing.sourcePages.ex != null ? this.housing.sourcePages.ex : '',
      hostelworldId:
        this.housing.sourceIds.hw != null ? this.housing.sourceIds.hw : '',
      hostelworldWebsite:
        this.housing.sourcePages.hw != null ? this.housing.sourcePages.hw : '',
      travelclickId:
        this.housing.sourceIds.tc != null ? this.housing.sourceIds.tc : '',
      travelclickWebsite:
        this.housing.sourcePages.tc != null ? this.housing.sourcePages.tc : '',
      giataId:
        this.housing.sourceIds.gi != null ? this.housing.sourceIds.gi : '',
      giataWebsite:
        this.housing.sourcePages.gi != null ? this.housing.sourcePages.gi : '',
    };
    this.form.setValue(vals);

    this.enableDisableFormCtrl();
  }

  private enableDisableFormCtrl() {
    const enable = this.housing != null;
    for (const key in this.form.controls) {
      if (this.form.controls.hasOwnProperty(key)) {
        if (enable) {
          this.form.controls[key].enable();
        } else {
          this.form.controls[key].disable();
        }
      }
    }
  }

  public returnEmptyStringWhenNull(element) {
    return element == null ? '' : element;
  }

  public extractValidationErrorsAsArray(errors) {
    if (errors == null) {
      return [];
    }

    const errorsToReturn = [];
    Object.keys(errors).forEach((key) => {
      errorsToReturn.push(errors[key]);
    });

    return errorsToReturn;
  }

  public showLoading() {
    this.isLoading = true;
    this.loadingIndicatorService.showLoadingBar();
  }

  public hideLoading() {
    this.isLoading = false;
    this.loadingIndicatorService.hideLoadingBar();
  }
}
